import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Lock, Copy, Check, LogIn, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

/**
 * /demo-access — internal page for showcasing demo personas.
 *
 * Credentials are NOT bundled in source. They are fetched at runtime from
 * `GET /api/auth/demo-logins`, which reads them from `DEMO_LOGINS` env on
 * the backend. Production can disable the endpoint via `DEMO_MODE=false`.
 */
export default function DemoAccess() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [busyEmail, setBusyEmail] = useState(null);
  const [copied, setCopied] = useState(null);
  const [demoLogins, setDemoLogins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(true);

  useEffect(() => {
    if (user) navigate("/");
  }, [user, navigate]);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/auth/demo-logins")
      .then((r) => {
        if (cancelled) return;
        setDemoLogins(Array.isArray(r.data?.logins) ? r.data.logins : []);
        setEnabled(r.data?.enabled !== false);
      })
      .catch(() => {
        if (cancelled) return;
        setDemoLogins([]);
        setEnabled(false);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const signIn = async (d) => {
    setBusyEmail(d.email);
    try {
      await login(d.email, d.password);
      navigate("/");
    } catch (e) {
      toast.error("Could not sign in — please try again.");
      setBusyEmail(null);
    }
  };

  const copy = async (d) => {
    try {
      await navigator.clipboard.writeText(`${d.email}\n${d.password}`);
      setCopied(d.email);
      setTimeout(() => setCopied((cur) => (cur === d.email ? null : cur)), 1500);
    } catch {
      toast.error("Clipboard not available in this browser");
    }
  };

  return (
    <div className="min-h-screen bg-secondary/40" data-testid="demo-access-page">
      <div className="max-w-3xl mx-auto p-6 lg:p-10">
        <Link
          to="/login"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
          data-testid="demo-back-to-login"
        >
          <ArrowLeft className="h-4 w-4" /> Back to sign in
        </Link>

        <header className="mt-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-[11px] uppercase tracking-[0.18em] font-semibold">
            <Lock className="h-3 w-3" /> Demo access
          </div>
          <h1 className="mt-3 text-3xl lg:text-4xl font-display font-bold tracking-tight">
            Try every role.
          </h1>
          <p className="text-muted-foreground mt-1.5 max-w-xl">
            Each card opens the dashboard for that persona. Use these to evaluate the platform —
            real customers should be created from the Super Admin → Manage → Users screen.
          </p>
        </header>

        <div className="mt-3 rounded-xl border border-amber-300/50 bg-amber-50 dark:bg-amber-950/30 p-3 flex items-start gap-2.5 text-sm" data-testid="demo-warning">
          <ShieldAlert className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
          <p className="text-amber-900 dark:text-amber-200">
            <span className="font-semibold">Demo only.</span> Do not enter real patient data while signed in as one of these
            accounts. Change every demo password before launching to production.
          </p>
        </div>

        {loading && (
          <p className="mt-8 text-sm text-muted-foreground" data-testid="demo-loading">Loading demo accounts…</p>
        )}

        {!loading && !enabled && (
          <Card className="mt-6 p-6 rounded-2xl" data-testid="demo-disabled">
            <h2 className="font-display font-semibold text-lg">Demo access is disabled.</h2>
            <p className="text-sm text-muted-foreground mt-1.5">
              This environment is running in production mode. Please sign in with your own account.
            </p>
            <Link to="/login">
              <Button className="mt-4 rounded-full" data-testid="demo-disabled-go-login">
                <LogIn className="h-4 w-4 mr-1.5" /> Go to sign in
              </Button>
            </Link>
          </Card>
        )}

        {!loading && enabled && demoLogins.length === 0 && (
          <Card className="mt-6 p-6 rounded-2xl" data-testid="demo-empty">
            <h2 className="font-display font-semibold text-lg">No demo accounts configured.</h2>
            <p className="text-sm text-muted-foreground mt-1.5">
              Set the <code className="px-1 py-0.5 bg-secondary rounded">DEMO_LOGINS</code> environment variable on the backend to advertise demo accounts here.
            </p>
          </Card>
        )}

        {!loading && enabled && demoLogins.length > 0 && (
          <div className="mt-6 grid sm:grid-cols-2 gap-4">
            {demoLogins.map((d) => (
              <Card key={d.email} className="p-5 rounded-2xl border-border bg-card/95 hover:shadow-md transition-shadow" data-testid={`demo-card-${(d.label || "role").toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">Role</div>
                    <h2 className="font-display font-semibold text-lg mt-0.5">{d.label}</h2>
                  </div>
                  <button
                    type="button"
                    onClick={() => copy(d)}
                    className="text-muted-foreground hover:text-foreground p-1.5 rounded-lg hover:bg-secondary"
                    title="Copy credentials"
                    data-testid={`demo-copy-${(d.label || "role").toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {copied === d.email ? <Check className="h-4 w-4 text-[#16A34A]" /> : <Copy className="h-4 w-4" />}
                  </button>
                </div>
                {d.desc && (
                  <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{d.desc}</p>
                )}

                <dl className="mt-4 space-y-1.5 text-xs">
                  <div className="flex justify-between gap-3">
                    <dt className="text-muted-foreground">Email</dt>
                    <dd className="font-mono-num text-foreground">{d.email}</dd>
                  </div>
                  <div className="flex justify-between gap-3">
                    <dt className="text-muted-foreground">Password</dt>
                    <dd className="font-mono-num text-foreground">{d.password}</dd>
                  </div>
                </dl>

                <Button
                  onClick={() => signIn(d)}
                  disabled={busyEmail === d.email}
                  className="w-full mt-4 rounded-full"
                  data-testid={`demo-signin-${(d.label || "role").toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {busyEmail === d.email
                    ? "Signing in…"
                    : <><LogIn className="h-4 w-4 mr-1.5" /> Sign in as {d.label}</>}
                </Button>
              </Card>
            ))}
          </div>
        )}

        <p className="text-[11px] text-muted-foreground mt-8 text-center">
          Have your own account? <Link to="/login" className="text-primary hover:underline">Sign in here</Link>.
        </p>
      </div>
    </div>
  );
}
